import type { EventId, MarketId, TemplateView } from '@ps/isp-web-services-types';
import classNames from 'clsx';
import React from 'react';
import { useSelector } from 'react-redux';

import { useRegulatoryIntl } from '@swf/core/hooks/useRegulatoryIntl';
import type { RootState } from '@swf/core/store';
import { Title } from '@swf/sports/components';
import { ExpandablePanel } from '@swf/sports/components/ExpandablePanel/ExpandablePanel';
import Header from '@swf/sports/components/GroupedTemplatePanels/components/Header/Header';
import { useEventMarketTemplatesWidget } from '@swf/sports/models/market-templates/eventBased';
import { isEventTemplate } from '@swf/sports/types/templates';
import { isCompoundMarketTemplate } from '@swf/sports/utils/isCompoundMarketTemplate';
import { notEmpty } from '@swf/sports/utils/notEmpty';

import useGetMarket from '../utils/useGetMarket';

import { TXTWCL_N0_logo } from './CompetitionPanel.messages.json';
import styles from './CompetitionPanel.scss';
import DatePanel from './DatePanel';

interface Props {
  competitionGroup: TemplateView[][];
  eventId?: EventId;
  marketId?: MarketId;
  marketIds?: MarketId[];
}

const CompetitionPanel: React.FC<Props> = ({ competitionGroup, eventId, marketId, marketIds }) => {
  const { widgetSelect } = useEventMarketTemplatesWidget();
  const market = useGetMarket({ marketId, marketIds });
  const event = useSelector((state: RootState) =>
    widgetSelect.event(state, market ? market.eventId : eventId ?? -1)
  );
  const competition = useSelector((state: RootState) =>
    widgetSelect.competition(state, event.competitionId)
  );

  const { competitionName: panelName, logoUrl } = competition;
  const firstTemplateInCompetition = competitionGroup[0][0].template;
  const { formatMessage } = useRegulatoryIntl();

  return (
    <div
      className={classNames(styles.panel)}
      key={panelName}
      data-testid="match-filters-competition-panel"
    >
      <ExpandablePanel
        isOpenInitially
        title={
          <Title hasIcon={!!logoUrl}>
            {logoUrl ? (
              <img
                src={logoUrl}
                className={styles.logoImg}
                alt={formatMessage(TXTWCL_N0_logo, { competitionName: panelName })}
              />
            ) : null}
            <span>{panelName}</span>
          </Title>
        }
      >
        {market && !isCompoundMarketTemplate(firstTemplateInCompetition) ? (
          <Header market={market} eventTypeId={market.eventTypeId} />
        ) : null}

        {competitionGroup.map((dateGroup, index) => {
          const firstTemplateDateGroup = dateGroup[0].template;

          if (isEventTemplate(firstTemplateDateGroup)) {
            return (
              <DatePanel
                key={`${firstTemplateDateGroup.eventId}_panel}`}
                templates={dateGroup}
                isFirstDateInCompetition={index === 0}
              />
            );
          }

          return isCompoundMarketTemplate(firstTemplateDateGroup) ? (
            <DatePanel
              key={`${firstTemplateDateGroup.marketIds.join('_')}_panel}`}
              templates={dateGroup}
              marketIds={firstTemplateDateGroup.marketIds.filter(notEmpty)}
              isFirstDateInCompetition={index === 0}
            />
          ) : (
            <DatePanel
              key={`${firstTemplateDateGroup.marketId}_panel}`}
              templates={dateGroup}
              marketId={firstTemplateDateGroup.marketId}
              isFirstDateInCompetition={index === 0}
            />
          );
        })}
      </ExpandablePanel>
    </div>
  );
};

export default CompetitionPanel;
